import React, { MutableRefObject } from 'react';
import { GridItem, Icon, Tooltip } from '@chakra-ui/react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  ContributionClassesAndRolesDDL,
  ContributionClassName,
} from 'types/contribution';
import { FormSubtitle, InputField } from 'app/components/Core';
import { ReactComponent as InformationIcon } from 'resources/images/info-icon.svg';
import { ChakraAutocompleteField } from 'app/components/Core/ChakraAutocompleteField/ChakraAutocompleteField';
import { SearchableProfile } from 'types/notification.types';
import {
  ContributionFormData,
  PerformerRoleDetailHistory,
  SetPerformerRoleDetailHistory,
} from '../types';
import { getIDFieldInfo, getIsniFieldInfo, getRoleFieldsInfo } from './utils';
import { IPNField } from '../IPNField';
import { PerformerDetailsField } from '../PerformerDetailsField';

interface RoleFieldsProps {
  contributionClassName: ContributionClassName;
  contributionClassesAndRolesDDL: ContributionClassesAndRolesDDL;
  roleFieldRef?: MutableRefObject<HTMLDivElement | null>;
  contributor?: SearchableProfile | null;
  performerRoleDetailHistory: PerformerRoleDetailHistory;
  setPerformerRoleDetailHistory: SetPerformerRoleDetailHistory;
}

export const RoleFields: React.FC<RoleFieldsProps> = ({
  contributionClassName,
  contributionClassesAndRolesDDL,
  roleFieldRef,
  contributor,
  performerRoleDetailHistory,
  setPerformerRoleDetailHistory,
}) => {
  const {
    formState: { errors },
    register,
    control,
    setValue,
  } = useFormContext<ContributionFormData>();

  const fieldsInfo = getRoleFieldsInfo(
    contributionClassName,
    contributionClassesAndRolesDDL
  );
  const performerRoleType = useWatch({
    control,
    name: 'performerRole',
  });
  const performerDetailField =
    fieldsInfo?.detailField &&
    fieldsInfo.detailField(performerRoleType?.value as string);

  const IDFieldInfo = getIDFieldInfo(contributionClassName);
  const isniFieldInfo = getIsniFieldInfo(contributionClassName);

  const getRoleDetailPreviousOption = (performerRoleType: string) => {
    const roleDetailPreviousValue =
      performerRoleDetailHistory[performerRoleType];

    return (
      fieldsInfo?.detailField &&
      fieldsInfo
        .detailField(performerRoleType)
        ?.options?.find((option) => option.value === roleDetailPreviousValue)
    );
  };

  return fieldsInfo ? (
    <>
      <GridItem w="full" gridColumn="1 / 4">
        <FormSubtitle containerStyleProps={{ width: 'full', pt: 4 }}>
          {fieldsInfo.subtitle}
        </FormSubtitle>
      </GridItem>
      <GridItem ref={roleFieldRef}>
        {fieldsInfo.roleOptions ? (
          <ChakraAutocompleteField
            key={fieldsInfo.fieldName}
            control={control}
            name={fieldsInfo.fieldName}
            label={fieldsInfo.fieldLabel}
            options={fieldsInfo.roleOptions}
            isInvalid={Boolean(errors?.[fieldsInfo.fieldName])}
            errorMessage={errors?.[fieldsInfo.fieldName]?.message}
            tooltip={fieldsInfo.tooltip}
            isClearable
            dataTestId="className-role-field"
            onChange={(value) => {
              if (value !== null && value?.value !== performerRoleType?.value) {
                setValue('performerRoleDetail', null);
              }

              if (value?.value) {
                const roleDetailPreviousOption = getRoleDetailPreviousOption(
                  value?.value as string
                );
                if (roleDetailPreviousOption) {
                  setValue('performerRoleDetail', roleDetailPreviousOption);
                }
              } else {
                if (performerRoleType?.value) {
                  setPerformerRoleDetailHistory((state) => {
                    const { [performerRoleType.value]: _, ...rest } = state;
                    return rest;
                  });
                }

                setValue('performerRoleDetail', null);
              }
            }}
          />
        ) : (
          <InputField
            type="text"
            label={fieldsInfo.fieldLabel}
            isInvalid={Boolean(errors?.[fieldsInfo.fieldName])}
            errorMessage={errors?.[fieldsInfo.fieldName]?.message}
            overflow="hidden"
            textOverflow="ellipsis"
            width="full"
            data-testid="className-role-field"
            {...register(fieldsInfo.fieldName)}
          />
        )}
      </GridItem>
      {contributionClassName !== ContributionClassName.PERFORMER &&
        IDFieldInfo && (
          <GridItem>
            <IPNField
              IPNFieldInfo={IDFieldInfo}
              disabled={Boolean(contributor?.guid)}
            />
          </GridItem>
        )}
      <PerformerDetailsField
        setPerformerRoleDetailHistory={setPerformerRoleDetailHistory}
        performerRoleType={performerRoleType}
        detailField={performerDetailField}
      />
      {contributionClassName === ContributionClassName.PERFORMER &&
        IDFieldInfo && (
          <GridItem>
            <IPNField
              IPNFieldInfo={IDFieldInfo}
              disabled={Boolean(contributor?.guid)}
            />
          </GridItem>
        )}
      {isniFieldInfo && (
        <GridItem>
          <InputField
            key={isniFieldInfo.isniFieldName}
            label={isniFieldInfo.isniFieldLabel}
            isInvalid={Boolean(errors?.[isniFieldInfo.isniFieldName])}
            errorMessage={errors?.[isniFieldInfo.isniFieldName]?.message}
            rightElement={
              <Tooltip hasArrow label={isniFieldInfo.tooltip}>
                <Icon
                  as={InformationIcon}
                  cursor="pointer"
                  color="textSubdued"
                  width="16px"
                  height="17px"
                  data-testid="tooltip-icon"
                />
              </Tooltip>
            }
            isDisabled={Boolean(contributor?.guid)}
            data-testid="className-isni-field"
            overflow="hidden"
            textOverflow="ellipsis"
            width="full"
            {...register(isniFieldInfo.isniFieldName)}
          />
        </GridItem>
      )}
    </>
  ) : null;
};
